import React from "react";
import { Link } from "react-router-dom";
import "./styles/SecuritySolutions.css";

const defenseLayers = [
  {
    title: "DDoS Protection",
    text: "Protect business critical services from distributed denial of service attacks and help keep digital operations available during malicious traffic events.",
  },
  {
    title: "Security Syslog",
    text: "Centralize security log visibility so events across devices and systems can be tracked more clearly and investigated with better context.",
  },
  {
    title: "SIEM Solutions",
    text: "Correlate security activity, detect suspicious patterns, and improve visibility into events that may affect your network, users, or infrastructure.",
  },
  {
    title: "Next Generation Firewall",
    text: "Strengthen traffic inspection and policy enforcement with modern firewall capabilities designed for more advanced network defense.",
  },
];

const threatSignals = [
  {
    title: "Availability threats",
    text: "DDoS attacks can disrupt access to applications, websites, and digital services if traffic floods are not handled effectively.",
  },
  {
    title: "Visibility gaps",
    text: "Without centralized logging and event analysis, suspicious activity can be harder to identify and investigate in time.",
  },
  {
    title: "Evolving attack patterns",
    text: "Security environments need stronger detection and layered controls because threats continue to shift in scale and complexity.",
  },
  {
    title: "Operational pressure",
    text: "Managed support helps organizations respond more effectively when they need continuous monitoring and faster defensive action.",
  },
];

const architecturePoints = [
  "Traffic protection at the edge to reduce disruption risk",
  "Centralized event visibility through Syslog and SIEM",
  "Policy enforcement through next generation firewall controls",
  "Managed support to strengthen ongoing security operations",
];

const serviceOutcomes = [
  {
    label: "Stronger uptime protection",
    text: "Reduce the chance that service disruption impacts users, availability, or business continuity.",
  },
  {
    label: "Better threat visibility",
    text: "Use centralized logging and SIEM driven analysis to improve awareness across the environment.",
  },
  {
    label: "Layered network defense",
    text: "Combine DDoS mitigation, logging, SIEM, and firewall controls instead of relying on a single security layer.",
  },
];

const fitAreas = [
  {
    title: "Public facing platforms",
    text: "For businesses that depend on website availability, customer portals, or digital services staying online.",
  },
  {
    title: "Growing IT environments",
    text: "For organizations that need stronger monitoring, better control visibility, and a more structured security posture.",
  },
  {
    title: "Security conscious operations",
    text: "For teams that want managed support and stronger defensive coverage without relying only on internal monitoring.",
  },
];

export default function SecuritySolutions() {
  return (
    <div className="securityPage">
      <section className="securityHero">
        <div className="securityContainer securityHero__container">
          <div className="securityHero__content">
            <p className="securityEyebrow">Security Solutions</p>

            <h1 className="securityHero__title">
              Layered protection built to defend uptime, visibility, and network resilience.
            </h1>

            <p className="securityHero__text">
              Our Security Solutions combine comprehensive DDoS protection with
              Security Syslog, SIEM solutions, next generation firewall
              capabilities, and managed support to help safeguard digital assets
              and maintain uninterrupted online operations. The result is a more
              resilient security posture built around visibility, protection, and
              ongoing defense.
            </p>

            <div className="securityHero__actions">
              <Link to="/contact" className="securityBtn securityBtn--primary">
                Talk to Our Team
              </Link>
              <Link to="/services" className="securityBtn securityBtn--secondary">
                Explore Services
              </Link>
            </div>
          </div>

          <div className="securityHero__visual">
            <div className="securityRadarCard">
              <div className="securityRadarCard__header">
                <span className="securityBadge">Live defense layers</span>
                <span className="securityBadge securityBadge--soft">
                  Managed monitoring
                </span>
              </div>

              <div className="securityRadar">
                <div className="securityRadar__ring securityRadar__ring--one" />
                <div className="securityRadar__ring securityRadar__ring--two" />
                <div className="securityRadar__ring securityRadar__ring--three" />
                <div className="securityRadar__center" />

                <div className="securityRadar__point securityRadar__point--ddos">
                  DDoS
                </div>
                <div className="securityRadar__point securityRadar__point--siem">
                  SIEM
                </div>
                <div className="securityRadar__point securityRadar__point--fw">
                  NGFW
                </div>
                <div className="securityRadar__point securityRadar__point--logs">
                  Syslog
                </div>
              </div>

              <div className="securityRadarCard__footer">
                <div className="securityPulseItem">
                  <strong>Availability</strong>
                  <span>Protected against disruption focused traffic attacks</span>
                </div>
                <div className="securityPulseItem">
                  <strong>Visibility</strong>
                  <span>Improved through logging and correlated security analysis</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="securityStackSection">
        <div className="securityContainer">
          <div className="securitySectionHead securitySectionHead--center">
            <p className="securityEyebrow">Defense stack</p>
            <h2 className="securityTitle">
              Multiple security layers working together
            </h2>
            <p className="securityText">
              Effective network defense is stronger when availability
              protection, visibility tools, traffic controls, and managed
              support work as one coordinated security model.
            </p>
          </div>

          <div className="securityStackGrid">
            {defenseLayers.map((item, index) => (
              <article key={item.title} className="securityStackCard">
                <span className="securityStackCard__index">0{index + 1}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="securitySignalsSection">
        <div className="securityContainer securitySignalsWrap">
          <div className="securitySignalsIntro">
            <p className="securityEyebrow">Why it matters</p>
            <h2 className="securityTitle">
              Security pressure comes from both disruption and limited visibility
            </h2>
            <p className="securityText">
              Organizations need to defend service availability while also
              improving how threats are seen, analyzed, and responded to across
              the environment.
            </p>
          </div>

          <div className="securitySignalsGrid">
            {threatSignals.map((item) => (
              <article key={item.title} className="securitySignalCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="securityArchitectureSection">
        <div className="securityContainer">
          <div className="securityArchitectureBand">
            <div className="securityArchitectureBand__left">
              <p className="securityEyebrow">Security architecture</p>
              <h2 className="securityTitle">
                Built around protection, visibility, and ongoing managed support
              </h2>
              <p className="securityText">
                DDoS mitigation, event logging, SIEM analysis, and next generation
                firewall capabilities each solve different security needs. When
                combined in one service approach, they create a stronger and
                more resilient defense model.
              </p>
            </div>

            <div className="securityArchitectureBand__right">
              <ul className="securityArchitectureList">
                {architecturePoints.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="securityOutcomesSection">
        <div className="securityContainer">
          <div className="securitySectionHead">
            <p className="securityEyebrow">Service outcomes</p>
            <h2 className="securityTitle">
              What this security model helps improve
            </h2>
            <p className="securityText">
              The value of a layered security service is not only threat
              blocking. It also supports continuity, visibility, and stronger
              operational confidence across digital infrastructure.
            </p>
          </div>

          <div className="securityOutcomeGrid">
            {serviceOutcomes.map((item) => (
              <article key={item.label} className="securityOutcomeCard">
                <strong>{item.label}</strong>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="securityFitSection">
        <div className="securityContainer">
          <div className="securitySectionHead securitySectionHead--center">
            <p className="securityEyebrow">Best fit</p>
            <h2 className="securityTitle">
              Where this service creates the most value
            </h2>
            <p className="securityText">
              These Security Solutions are especially useful where uptime,
              digital asset protection, and stronger network visibility are
              critical to operations.
            </p>
          </div>

          <div className="securityFitGrid">
            {fitAreas.map((item) => (
              <article key={item.title} className="securityFitCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="securityCta">
        <div className="securityContainer securityCta__container">
          <div>
            <p className="securityEyebrow securityEyebrow--light">Next step</p>
            <h2 className="securityCta__title">
              Strengthen your network defense with a more complete security approach
            </h2>
            <p className="securityCta__text">
              If your organization needs stronger DDoS protection, better event
              visibility, next generation firewall support, and managed security
              guidance, ComNetworks.net can help shape the right solution for
              your environment.
            </p>
          </div>

          <div className="securityCta__actions">
            <Link to="/contact" className="securityBtn securityBtn--primary">
              Contact Us
            </Link>
            <Link to="/services" className="securityBtn securityBtn--ghost">
              View More Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
